import { useEffect, useRef } from 'react';

type ActivateInfo = {
  reason: 'visible' | 'pageshow' | 'initial';
  sinceHiddenMs?: number;
};

type Options = {
  onDeactivate?: () => void;
  dedupeWindowMs?: number;
  onDebug?: (msg: string) => void;
};

function usePageActivation(onActivate: (info: ActivateInfo) => void, opts?: Options): void {
  const { onDeactivate, dedupeWindowMs = 150, onDebug } = opts ?? {};

  const lastHiddenAtRef = useRef<number | null>(null);
  const lastFiredAtRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    lastHiddenAtRef.current = document.visibilityState === 'hidden' ? Date.now() : null;
    onDebug?.(`init visibility=${document.visibilityState}`);
    const maybeActivate = (reason: ActivateInfo['reason']): void => {
      const now = Date.now();
      if (now - lastFiredAtRef.current < dedupeWindowMs) {
        onDebug?.(`skip (dedupe): ${reason}`);
        return;
      }
      lastFiredAtRef.current = now;

      const sinceHiddenMs = lastHiddenAtRef.current !== null ? now - lastHiddenAtRef.current : undefined;
      onDebug?.(`activate: ${reason}, sinceHiddenMs=${sinceHiddenMs}`);
      onActivate({ reason, sinceHiddenMs });
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAtRef.current = Date.now();
        onDebug?.('hidden');
        onDeactivate?.();
      } else if (document.visibilityState === 'visible') {
        onDebug?.('visible');
        maybeActivate('visible');
      }
    };

    const handlePageShow = (e: Event): void => {
      const persisted = (e as PageTransitionEvent).persisted ?? false;
      onDebug?.(`pageshow persisted=${persisted}`);
      maybeActivate('pageshow');
    };

    const handleBlur = (): void => {
      onDebug?.('blur');
      onDeactivate?.();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    if (onDeactivate) window.addEventListener('blur', handleBlur);

    let id: ReturnType<typeof setTimeout> | undefined;
    if (document.visibilityState === 'visible' && lastFiredAtRef.current === 0) {
      id = setTimeout(() => maybeActivate('initial'), 0);
    }

    return (): void => {
      if (id) clearTimeout(id);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      if (onDeactivate) window.removeEventListener('blur', handleBlur);
    };
  }, [onActivate, onDeactivate, dedupeWindowMs, onDebug]);
}
export default usePageActivation;
